/**
 * Milestone 3: user notifications (`social.notifications`), the external agent inbox (`jobs.agent_inbox`),
 * agent webhooks (`agents.agent_webhooks`, `agents.webhook_deliveries`) and agent-initiated token launches.
 */
import { z } from 'zod'
import { Cursor, IsoTimestamp, PageLimit, Uuid } from '../primitives.js'
import { InboxStatus, NotificationKind, WebhookDeliveryStatus } from '../enums.js'
import { AgentSummary, JsonObject } from './common.js'
import { InboxItem } from './manage.js'

// Notifications ----------------------------------------------------------------------

export const Notification = z.object({
  id: Uuid,
  kind: NotificationKind,
  title: z.string(),
  body: z.string(),
  payload: JsonObject,
  agent: AgentSummary.nullable(),
  /** `accounts.wallets.id` the notification is about, if any. */
  walletId: Uuid.nullable(),
  readAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
})
export type Notification = z.output<typeof Notification>

const BoolParam = z.enum(['true', 'false']).transform((v) => v === 'true')

export const NotificationsQuery = z.object({ cursor: Cursor.optional(), limit: PageLimit, unread: BoolParam.optional() })

export const UnreadCount = z.object({ count: z.number().int().nonnegative() })
export type UnreadCount = z.output<typeof UnreadCount>

/** POST /me/notifications/read: exactly one of `ids` or `all: true`. */
export const MarkNotificationsRead = z
  .object({ ids: z.array(Uuid).min(1).max(200).optional(), all: z.boolean().optional() })
  .refine((b) => (b.ids !== undefined) !== (b.all === true), { message: 'give either ids or all: true' })
export type MarkNotificationsRead = z.input<typeof MarkNotificationsRead>

export const MarkedRead = z.object({ updated: z.number().int().nonnegative() })
export type MarkedRead = z.output<typeof MarkedRead>

/** One row per notification kind (defaults when the user never changed it). Email comes later. */
export const NotificationPreference = z.object({
  kind: NotificationKind,
  inApp: z.boolean(),
  webhook: z.boolean(),
  /** Web push to the user's registered browsers (social layer; absent from older servers = off). */
  push: z.boolean().default(false),
  /** The agent's alert message in the user's conversation thread (Wave C, `conversation.alert`; absent = on). */
  chat: z.boolean().optional(),
})
export type NotificationPreference = z.output<typeof NotificationPreference>

export const PatchNotificationPreferences = z.object({
  preferences: z.array(z.object({ kind: NotificationKind, inApp: z.boolean().optional(), webhook: z.boolean().optional(), push: z.boolean().optional(), chat: z.boolean().optional() })).min(1),
})
export type PatchNotificationPreferences = z.input<typeof PatchNotificationPreferences>

// External agent inbox ---------------------------------------------------------------

/** GET /agent/inbox: `jobs.agent_inbox` rows (permission changes, budget, revisions, system). Ack marks one processed. */
export const InboxMessage = InboxItem
export type InboxMessage = z.output<typeof InboxMessage>

export const InboxQuery = z.object({ cursor: Cursor.optional(), limit: PageLimit, status: InboxStatus.optional() })

// Webhooks ---------------------------------------------------------------------------

/** Events an external agent can subscribe a webhook to. `inbox` mirrors every new inbox message. */
export const WebhookEvent = z.enum([
  'inbox',
  'proposal.approved',
  'proposal.refused',
  'proposal.executed',
  'proposal.failed',
  'permission.changed',
  'budget.exhausted',
  'token.launched',
  'system',
])
export type WebhookEvent = z.output<typeof WebhookEvent>

export const Webhook = z.object({
  id: Uuid,
  url: z.string(),
  events: z.array(WebhookEvent),
  enabled: z.boolean(),
  createdAt: IsoTimestamp,
  lastDeliveryAt: IsoTimestamp.nullable(),
  failureCount: z.number().int().nonnegative(),
})
export type Webhook = z.output<typeof Webhook>

/**
 * POST /agent/webhooks. `secret` keys the HMAC-SHA256 signature; it is stored encrypted (AES-256-GCM,
 * `agents.agent_webhooks.secret_enc`) because the runtime must recover it to sign deliveries, and is never returned.
 */
export const CreateWebhook = z.object({
  url: z.url({ protocol: /^https?$/ }).max(2000),
  secret: z.string().min(16).max(200),
  events: z.array(WebhookEvent).min(1),
})
export type CreateWebhook = z.input<typeof CreateWebhook>

export const WebhookDelivery = z.object({
  id: Uuid,
  webhookId: Uuid,
  event: z.string(),
  status: WebhookDeliveryStatus,
  attempts: z.number().int().nonnegative(),
  nextAttemptAt: IsoTimestamp,
  responseCode: z.number().int().nullable(),
  createdAt: IsoTimestamp,
  deliveredAt: IsoTimestamp.nullable(),
})
export type WebhookDelivery = z.output<typeof WebhookDelivery>

/**
 * Body POSTed to a webhook URL. Headers: `X-Algoboo-Event`, `X-Algoboo-Delivery` (delivery id),
 * `X-Algoboo-Timestamp` (unix seconds) and `X-Algoboo-Signature: t=<timestamp>,v1=<hex HMAC-SHA256(secret, "<timestamp>.<body>")>`
 * (several `v1` entries may appear during secret rotation; `verifyWebhookSignature` in @algoboo/notify checks it).
 */
export const WebhookEnvelope = z.object({
  id: Uuid,
  event: z.string(),
  agentId: Uuid,
  createdAt: IsoTimestamp,
  data: JsonObject,
})
export type WebhookEnvelope = z.output<typeof WebhookEnvelope>

export const WEBHOOK_SIGNATURE_HEADER = 'X-Algoboo-Signature' as const

